<template>
    <hh-accent-panel class="dashboard-address">
        <section class="dashboard-address__header">
            <div class="dashboard-address__header__bullet">
                <div class="dashboard-address__header__bullet__icon-wrapper">
                    <i class="dashboard-address__header__bullet__icon fas fa-house" />
                </div>
            </div>
            <h3 class="dashboard-address__header__main dashboard-address__header__title">
                {{ address.asString() }}
            </h3>
            <div class="dashboard-address__header__buttons">
                <hh-btn class="dashboard-address__header__buttons__button dashboard-address__header__buttons__button--edit"
                        :track-operation-state="true"
                        :operation-in-progress="addressIsLoading"
                        :text="myResources.editAddressButtonText"
                        :short-text="myResources.editAddressShortButtonText"
                        @click="onEditAddressClick" />
            </div>
        </section>
        <section class="dashboard-address__renovations">
            <div v-if="hasRenovations">
                <ul class="dashboard-address__renovations__list">
                    <dashboard-renovation v-for="renovation in address.renovations"
                                          class="dashboard-address__renovations__list__item"
                                          :key="renovation.id"
                                          :renovation="renovation" />
                </ul>
            </div>
            <div v-else>
                <p class="dashboard-address__renovations__none">
                    {{ myResources.noRenovationsText }}
                </p>
            </div>
        </section>
        <section class="dashboard-address__renovation-buttons">
            <hh-btn class="dashboard-address__renovation-buttons__button dashboard-address__renovation-buttons__button--new"
                    :track-operation-state="true"
                    :operation-in-progress="newPlanIsLoading"
                    :text="myResources.newPlanButtonText"
                    @click="onNewPlanClick" />
        </section>
        <section class="dashboard-address__footer" />
    </hh-accent-panel>
</template>

<script>

import Property from '@/models/property'

import ResourcesHelper from '@/components/resources-helper'

import PlanBuilderManager from '@/plan-builder-manager'
import ProgressManager from '@/progress-manager'
import PropertyManager from '@/property-manager'

import DashboardRenovation from '@/components/dashboard/dashboard-renovation'

export default {

    mixins: [
        ResourcesHelper,
    ],

    props: {

        /**
         * The property to display details for.
         * @type {Property|{}}
         */
        address: {
            type: Property,
            required: true,
        },
    },

    data() {
        return {
            /**
             * @type {Boolean}
             */
            addressIsLoading: false,

            /**
             * @type {Boolean}
             */
            newPlanIsLoading: false,
        }
    },

    computed: {

        /**
         * @returns {boolean} True if the address has renovations data.
         */
        hasRenovations() {
            return this.address.renovations && this.address.renovations.length > 0
        },

        /**
         * Return the resources object for this page.
         * @return {DashboardAddressResources}
         */
        myResources() {
            return this.parseResources('components.dashboard.address')
        },
    },

    methods: {

        /**
         * Switches the address and loads into state so that the Plan Builder and Address pages can be directly loaded.
         * @returns {Promise<void>}
         * @private
         */
        async _preloadActivePropertyAsync() {
            ProgressManager.clearVisitedRoutes()
            const property = await PropertyManager.switchChosenPropertyAsync(this.address.id)
            this.$logger.logObject(property, `Property loaded with ID ${ this.address.id }`, 'Dashboard')

            if (property.isServedByScheme())
                await PropertyManager.loadHomeDataIfNeededOrForcedAsync(this.address.id, true)

            if (this.scheme.hasFixedBudget)
                await PlanBuilderManager.modifyBudgetAsync(this.$scheme.fixedBudget)

            if (this.scheme.hasFixedMotivation)
                await PlanBuilderManager.setMotivationAsync(this.$scheme.fixedMotivation)
        },

        /**
         * Triggers when clicking Edit Details.
         */
        onEditAddressClick() {
            this.addressIsLoading = true
            this.$logger.log(`Loading address with ID ${ this.address.id }`, 'Dashboard')
            this.$nextTick(async() => {
                await this._preloadActivePropertyAsync()
                this.$router.push({ name: 'MyHome', query: { id: this.address.id.toString() } })
            })
        },

        /**
         * Triggers when clicking Build New Plan.
         */
        onNewPlanClick() {
            this.newPlanIsLoading = true
            this.$logger.log(`Creating a new plan for address with ID ${ this.address.id }`, 'Dashboard')
            this.$nextTick(async() => {
                await this._preloadActivePropertyAsync()
                this.$router.push({ name: 'PlanBuilder' })
            })
        },
    },

    components: {
        DashboardRenovation,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\_dashboard-address.scss

.dashboard-address {
    clear: both;
    $bullet-width: 3.8rem;
    padding-bottom: 1rem;

    @include bp-min-mid {
        padding-bottom: 0.5rem;
    }

    @include element('header') {
        @include bulleted-dashboard-layout-core($bullet-width);

        @include nested-element('buttons', 'button') {
            @include bulleted-dashboard-button-widths;
        }
    }

    @include element('renovations') {

        @include element('list') {
            @include unstyle-list();
            margin-top: 0.5rem;

            > li {
                padding-bottom: 1.5rem;
                margin-top: 1.5rem;

                &:first-child {
                    margin-top: 0;
                }

                &:last-child {
                    padding-bottom: 0;
                }
            }
        }

        @include element('none') {
            font-size: 0.9rem;
            margin-top: 0.5rem;

            @include bp-min-mid {
                padding-left: $bullet-width;
            }
        }
    }

    @include element('renovation-buttons') {
        margin: 0.5rem 0 0 0;
        float: right;

        @include bp-min-mid {
            padding-left: $bullet-width;
        }

        @include element('button') {
            @include bulleted-dashboard-button-widths;
        }
    }

    @include element('footer') {
        clear: both;
    }
}

</style>
